import React, { useState, useMemo } from 'react'
import {
  Box,
  Card,
  Chip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Button,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import BoltIcon from '@mui/icons-material/Bolt'
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import EuroIcon from '@mui/icons-material/Euro'
import { useTranslation } from 'react-i18next'
import WTypography from '../../../Components/whiteTypography'

function ActionsPrioritaires({ actions, onCreateProject, onSimulate }) {
  const { t } = useTranslation('layout')
  const [sortMode, setSortMode] = useState('impact')
  const [filterHorizon, setFilterHorizon] = useState('all')
  const [expandedId, setExpandedId] = useState(actions[0]?.id || null)

  const filteredSortedActions = useMemo(() => {
    let arr = [...actions]
    if (filterHorizon !== 'all') {
      arr = arr.filter((a) => a.horizon === filterHorizon)
    }
    if (sortMode === 'impact') arr.sort((a, b) => b.impact - a.impact)
    else if (sortMode === 'facilite') arr.sort((a, b) => b.facilite - a.facilite)
    else if (sortMode === 'gain') arr.sort((a, b) => b.gainAnnuel - a.gainAnnuel)
    return arr
  }, [actions, sortMode, filterHorizon])

  return (
    <Card
      sx={{
        background: '#0e1a36',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: 3,
        color: 'white',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <WTypography variant="overline" sx={{ color: '#a0b4d6 !important', letterSpacing: 1.5 }}>
            {t('conseillerPatrimonial.actions.overline')}
          </WTypography>
          <WTypography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
            {t('conseillerPatrimonial.actions.title')}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important', mt: 0.5 }}>
            {t('conseillerPatrimonial.actions.subtitle')}
          </WTypography>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          <ToggleButtonGroup
            value={sortMode}
            exclusive
            size="small"
            onChange={(_, v) => v && setSortMode(v)}
            sx={toggleStyles}
          >
            <ToggleButton value="impact">{t('conseillerPatrimonial.actions.sortImpact')}</ToggleButton>
            <ToggleButton value="facilite">{t('conseillerPatrimonial.actions.sortFacilite')}</ToggleButton>
            <ToggleButton value="gain">{t('conseillerPatrimonial.actions.sortGain')}</ToggleButton>
          </ToggleButtonGroup>

          <ToggleButtonGroup
            value={filterHorizon}
            exclusive
            size="small"
            onChange={(_, v) => v && setFilterHorizon(v)}
            sx={toggleStyles}
          >
            <ToggleButton value="all">{t('conseillerPatrimonial.actions.filterAll')}</ToggleButton>
            <ToggleButton value="immediate">{t('conseillerPatrimonial.actions.filterImmediate')}</ToggleButton>
            <ToggleButton value="short">{t('conseillerPatrimonial.actions.filterShort')}</ToggleButton>
            <ToggleButton value="medium">{t('conseillerPatrimonial.actions.filterMedium')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={1.5}>
        {filteredSortedActions.map((action, idx) => (
          <ActionRow
            key={action.id}
            action={action}
            rank={idx + 1}
            isExpanded={expandedId === action.id}
            onToggle={() => setExpandedId(expandedId === action.id ? null : action.id)}
            onCreateProject={() => onCreateProject?.(action)}
            onSimulate={() => onSimulate?.(action)}
            t={t}
          />
        ))}
      </Box>
    </Card>
  )
}

function ActionRow({ action, rank, isExpanded, onToggle, onCreateProject, onSimulate, t }) {
  const horizonColor =
    { immediate: '#22c55e', short: '#0ea5e9', medium: '#fbbf24', long: '#a78bfa' }[action.horizon] || '#94a3b8'

  return (
    <Box
      sx={{
        bgcolor: isExpanded ? 'rgba(99,123,177,0.12)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${isExpanded ? 'rgba(126,156,219,0.5)' : 'rgba(255,255,255,0.06)'}`,
        borderRadius: 2,
        transition: 'all .2s',
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          p: 2,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: rank <= 3 ? 'rgba(251,191,36,0.2)' : 'rgba(255,255,255,0.08)',
            color: rank <= 3 ? '#fbbf24' : '#dbe5f7',
            fontWeight: 700,
            fontSize: 14,
            flexShrink: 0,
          }}
        >
          {rank}
        </Box>

        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <WTypography variant="body1" sx={{ fontWeight: 600 }}>
              {action.title}
            </WTypography>
            <Chip
              label={action.scoreImpact}
              size="small"
              icon={<BoltIcon sx={{ fontSize: 14, color: 'inherit !important' }} />}
              sx={{
                bgcolor: 'rgba(34,197,94,0.18)',
                color: '#4ade80',
                fontWeight: 600,
                height: 22,
              }}
            />
          </Box>
          <Box display="flex" gap={2} mt={0.5} flexWrap="wrap" alignItems="center">
            <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
              {action.category}
            </WTypography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon sx={{ fontSize: 12, color: horizonColor }} />
              <WTypography variant="caption" sx={{ color: `${horizonColor} !important`, fontWeight: 600 }}>
                {action.horizonLabel}
              </WTypography>
            </Box>
            {action.gainAnnuel > 0 && (
              <Box display="flex" alignItems="center" gap={0.5}>
                <EuroIcon sx={{ fontSize: 12, color: '#4ade80' }} />
                <WTypography variant="caption" sx={{ color: '#4ade80 !important', fontWeight: 600 }}>
                  +{action.gainAnnuel}€ / {t('conseillerPatrimonial.actions.year')}
                </WTypography>
              </Box>
            )}
          </Box>
        </Box>

        <ScoreBadge label={t('conseillerPatrimonial.actions.impactLabel')} value={action.impact} />
        <ScoreBadge label={t('conseillerPatrimonial.actions.faciliteLabel')} value={action.facilite} />

        <IconButton size="small" sx={{ color: '#a0b4d6' }}>
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
        <Box sx={{ p: 3, bgcolor: 'rgba(0,0,0,0.15)' }}>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '2fr 1fr' }} gap={3}>
            <Box>
              <WTypography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#dbe5f7 !important' }}>
                {t('conseillerPatrimonial.actions.whyTitle')}
              </WTypography>
              <WTypography variant="body2" sx={{ color: '#c1d0eb !important', lineHeight: 1.7 }}>
                {action.explanation}
              </WTypography>

              <Box mt={2} display="flex" flexWrap="wrap" gap={0.5}>
                {action.tags?.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(126,156,219,0.15)',
                      color: '#a8c1f0',
                      height: 22,
                      fontSize: 11,
                    }}
                  />
                ))}
              </Box>

              <Box mt={2}>
                <WTypography variant="caption" sx={{ color: '#a0b4d6 !important', display: 'block', mb: 0.5 }}>
                  {t('conseillerPatrimonial.actions.bestForLabel')}
                </WTypography>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  {action.bestFor?.map((bf) => (
                    <Box key={bf} display="flex" alignItems="center" gap={0.5}>
                      <TaskAltIcon sx={{ fontSize: 14, color: '#4ade80' }} />
                      <WTypography variant="caption" sx={{ color: '#dbe5f7 !important' }}>
                        {bf}
                      </WTypography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap={1.5}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'rgba(34,197,94,0.08)',
                  borderRadius: 1.5,
                  border: '1px solid rgba(34,197,94,0.2)',
                }}
              >
                <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
                  {t('conseillerPatrimonial.actions.estimatedGain')}
                </WTypography>
                <WTypography variant="h6" sx={{ fontWeight: 700, color: '#4ade80 !important' }}>
                  {action.gainDescription}
                </WTypography>
              </Box>

              <Button
                variant="contained"
                fullWidth
                endIcon={<ArrowOutwardIcon />}
                onClick={(e) => {
                  e.stopPropagation()
                  onCreateProject?.()
                }}
                sx={{
                  bgcolor: '#3b82f6',
                  '&:hover': { bgcolor: '#2563eb' },
                  fontWeight: 600,
                  textTransform: 'none',
                  py: 1.2,
                }}
              >
                {action.cta}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                onClick={(e) => {
                  e.stopPropagation()
                  onSimulate?.()
                }}
                sx={{
                  color: '#dbe5f7',
                  borderColor: 'rgba(255,255,255,0.2)',
                  textTransform: 'none',
                  '&:hover': { borderColor: '#dbe5f7' },
                }}
              >
                {t('conseillerPatrimonial.actions.simulateButton')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}

function ScoreBadge({ label, value }) {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: 50,
      }}
    >
      <WTypography variant="caption" sx={{ color: '#a0b4d6 !important', fontSize: 10 }}>
        {label}
      </WTypography>
      <WTypography variant="body2" sx={{ fontWeight: 700, color: '#dbe5f7 !important' }}>
        {value}/10
      </WTypography>
    </Box>
  )
}

const toggleStyles = {
  '& .MuiToggleButton-root': {
    color: '#a0b4d6',
    border: '1px solid rgba(255,255,255,0.1)',
    textTransform: 'none',
    fontSize: 12,
    px: 1.5,
    py: 0.5,
    '&.Mui-selected': {
      bgcolor: 'rgba(126,156,219,0.2)',
      color: '#fff',
      borderColor: 'rgba(126,156,219,0.5)',
      '&:hover': { bgcolor: 'rgba(126,156,219,0.3)' },
    },
  },
}

export default ActionsPrioritaires
